import type { Color } from "../common/color.js";
import type { DrawableTexture } from "../engine/texture.js";
import { Node2D, type Node2DOptions } from "./Node2D.js";
import { invert, transformRect, type Rect2D } from "./math2d.js";

export interface TileMapOptions extends Node2DOptions {
  texture: DrawableTexture;
  /** Tiles across and down. */
  columns: number;
  rows: number;
  tileWidth: number;
  tileHeight: number;
  /**
   * Tile index per cell, row-major. `-1` is empty. Shorter arrays leave the
   * rest empty; a missing array starts the map blank.
   */
  tiles?: readonly number[];
  /** Tiles across the atlas. Derived from the texture when omitted. */
  atlasColumns?: number;
  /** Gap between tiles in the atlas, in pixels. */
  spacing?: number;
  tint?: Color | null;
  opacity?: number;
}

/** One tile the renderer has been asked to draw. */
export interface VisibleTile {
  /** Top-left of the cell, in the map's own coordinates. */
  x: number;
  y: number;
  column: number;
  row: number;
  index: number;
  /** Where the tile lives in the atlas. */
  frame: Rect2D;
}

/**
 * A grid of tiles drawn from one atlas.
 *
 * The point of a tile map as a control rather than a few thousand `Sprite`
 * nodes is that it never *has* a few thousand nodes: the grid is a flat array
 * of indices, and only the cells the camera can see become quads. A 500x500
 * map is a quarter of a million tiles and costs the same to draw as the screen
 * can hold — a few hundred.
 */
export class TileMap extends Node2D {
  texture: DrawableTexture;
  tileWidth: number;
  tileHeight: number;
  atlasColumns: number;
  spacing: number;
  tint: Color | null;
  opacity: number;

  #columns: number;
  #rows: number;
  #tiles: Int32Array;

  constructor(options: TileMapOptions) {
    super(options);
    this.texture = options.texture;
    this.#columns = Math.max(1, Math.floor(options.columns));
    this.#rows = Math.max(1, Math.floor(options.rows));
    this.tileWidth = options.tileWidth;
    this.tileHeight = options.tileHeight;
    this.spacing = options.spacing ?? 0;
    this.atlasColumns =
      options.atlasColumns ??
      Math.max(1, Math.floor(options.texture.width / (options.tileWidth + this.spacing)));
    this.tint = options.tint ?? null;
    this.opacity = options.opacity ?? 1;

    this.#tiles = new Int32Array(this.#columns * this.#rows).fill(-1);
    if (options.tiles) {
      const n = Math.min(options.tiles.length, this.#tiles.length);
      for (let i = 0; i < n; i++) this.#tiles[i] = options.tiles[i]!;
    }
  }

  get columns(): number {
    return this.#columns;
  }

  get rows(): number {
    return this.#rows;
  }

  /** Width of the whole map in its own coordinates. */
  get mapWidth(): number {
    return this.#columns * this.tileWidth;
  }

  get mapHeight(): number {
    return this.#rows * this.tileHeight;
  }

  /** The tile at a cell, or -1 for empty or out of bounds. */
  at(column: number, row: number): number {
    if (column < 0 || column >= this.#columns || row < 0 || row >= this.#rows) return -1;
    return this.#tiles[row * this.#columns + column]!;
  }

  set(column: number, row: number, tile: number): void {
    if (column < 0 || column >= this.#columns || row < 0 || row >= this.#rows) return;
    this.#tiles[row * this.#columns + column] = tile;
  }

  fill(tile: number): void {
    this.#tiles.fill(tile);
  }

  /** The cell containing a point in the map's own coordinates. */
  cellAt(x: number, y: number): { column: number; row: number } {
    return {
      column: Math.floor(x / this.tileWidth),
      row: Math.floor(y / this.tileHeight),
    };
  }

  /** Where a tile index sits in the atlas. */
  frameOf(tile: number): Rect2D {
    const column = tile % this.atlasColumns;
    const row = Math.floor(tile / this.atlasColumns);
    return {
      x: column * (this.tileWidth + this.spacing),
      y: row * (this.tileHeight + this.spacing),
      width: this.tileWidth,
      height: this.tileHeight,
    };
  }

  override localBounds(): Rect2D {
    return { x: 0, y: 0, width: this.mapWidth, height: this.mapHeight };
  }

  /**
   * The non-empty tiles overlapping `visible` (world space), or all of them
   * when it is null.
   *
   * The window is worked out in cell coordinates first, so the cost is the
   * number of cells on screen rather than the number in the map.
   */
  *visibleTiles(visible: Rect2D | null): Generator<VisibleTile> {
    let first = { column: 0, row: 0 };
    let last = { column: this.#columns - 1, row: this.#rows - 1 };

    if (visible) {
      // The camera rectangle is in world space; bring it into the map's.
      const local = transformRect(invert(this.worldMatrix), visible);
      first = this.cellAt(local.x, local.y);
      last = this.cellAt(local.x + local.width, local.y + local.height);
      first = {
        column: Math.max(0, first.column),
        row: Math.max(0, first.row),
      };
      last = {
        column: Math.min(this.#columns - 1, last.column),
        row: Math.min(this.#rows - 1, last.row),
      };
    }

    for (let row = first.row; row <= last.row; row++) {
      for (let column = first.column; column <= last.column; column++) {
        const index = this.#tiles[row * this.#columns + column]!;
        if (index < 0) continue;
        yield {
          x: column * this.tileWidth,
          y: row * this.tileHeight,
          column,
          row,
          index,
          frame: this.frameOf(index),
        };
      }
    }
  }
}
